import React, { useEffect, useState } from 'react'
import { ArrowRight, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Background3D } from './Background3D'
import { useScrollY } from '@/hooks/useParallax'
import { smoothScrollToId } from '@/lib/smoothScroll'

const API_CODE = `const response = await fetch('https://api.neoserve.ai/v1/chat/completions', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ns_sg_live_...',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    model: 'ns-balanced',
    messages: [{ role: 'user', content: 'Hello!' }]
  })
})

const data = await response.json()
// "Hello! How can I help you today?"`

const REGIONS = [
  { code: 'PH', flag: 'ph' },
  { code: 'SG', flag: 'sg' },
  { code: 'AU', flag: 'au' },
  { code: 'MY', flag: 'my' },
  { code: 'ID', flag: 'id' },
  { code: 'TH', flag: 'th' },
  { code: 'VN', flag: 'vn' },
  { code: 'NZ', flag: 'nz' },
]

function RegionTicker() {
  const [index, setIndex] = useState(0)
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false)
      setTimeout(() => {
        setIndex(i => (i + 1) % REGIONS.length)
        setVisible(true)
      }, 300)
    }, 1800)
    return () => clearInterval(interval)
  }, [])

  return (
    <span
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-6px)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        display: 'inline-block',
      }}
    >
      <img
        src={`https://flagcdn.com/w40/${REGIONS[index].flag}.png`}
        alt={REGIONS[index].code}
        className="inline-block w-9 h-auto rounded-sm align-middle"
      />
    </span>
  )
}

type Token = { text: string; cls: string }

function tokenize(line: string): Token[] {
  const tokens: Token[] = []
  const parts = line.split(/('(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*"|\b(?:const|await|new|fetch)\b|\b(?:method|headers|body|model|messages|role|content)\b|\/\/.*$)/)
  for (const part of parts) {
    if (!part) continue
    if (part.startsWith('//')) {
      tokens.push({ text: part, cls: 'text-gray-500' })
    } else if (/^'.*'$/.test(part) || /^".*"$/.test(part)) {
      tokens.push({ text: part, cls: 'text-emerald-400' })
    } else if (/^(const|await|new|fetch)$/.test(part)) {
      tokens.push({ text: part, cls: 'text-violet-400' })
    } else if (/^(method|headers|body|model|messages|role|content)$/.test(part)) {
      tokens.push({ text: part, cls: 'text-sky-300' })
    } else {
      tokens.push({ text: part, cls: 'text-gray-200' })
    }
  }
  return tokens
}

export function Hero() {
  const scrollY = useScrollY()
  const scrollToWaitlist = () => smoothScrollToId('waitlist', 80)

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#050810]">
      {/* 3D particle canvas — moves slower than scroll (parallax) */}
      <div style={{ transform: `translateY(${scrollY * 0.25}px)`, willChange: 'transform' }} className="absolute inset-0">
        <Background3D />
      </div>

      {/* Ambient glows — each at a different parallax speed */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-[-10%] right-[-5%] w-[700px] h-[700px] bg-indigo-600/10 rounded-full blur-[120px]"
          style={{ transform: `translateY(${scrollY * 0.15}px)`, willChange: 'transform' }}
        />
        <div
          className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-violet-600/10 rounded-full blur-[100px]"
          style={{ transform: `translateY(${scrollY * -0.1}px)`, willChange: 'transform' }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-indigo-500/5 rounded-full blur-[80px]"
          style={{ transform: `translate(-50%, calc(-50% + ${scrollY * 0.08}px))`, willChange: 'transform' }}
        />
      </div>

      {/* Grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(rgba(148,163,184,1) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="container relative z-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          {/* Left — moves up slightly faster */}
          <div
            className="max-w-xl"
            style={{ transform: `translateY(${scrollY * -0.08}px)`, willChange: 'transform' }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-xs font-medium text-indigo-300 mb-7">
              <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 animate-pulse-slow" />
              Neoserve — Now in Early Access
              <ChevronRight className="h-3.5 w-3.5" />
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight text-balance">
              The AI API Platform{' '}
              <span className="gradient-text-bright">Built for Asia-Pacific</span>
            </h1>

            <p className="mt-6 text-lg text-gray-400 leading-relaxed text-balance">
              Access 20+ AI models through one OpenAI-compatible API. Pay in PHP, SGD, AUD, and more.
              Hosted on the edge. Designed for APAC developers and enterprises.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={scrollToWaitlist} className="bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-500/25">
                Get Early Access
                <ArrowRight className="h-5 w-5" />
              </Button>
            </div>

            {/* Stats */}
            <div className="mt-14 grid grid-cols-3 gap-6 border-t border-white/10 pt-8">
              <div>
                <p className="text-2xl font-bold text-white">20+</p>
                <p className="text-sm text-gray-500 mt-0.5">AI Models</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">8</p>
                <p className="text-sm text-gray-500 mt-0.5">Currencies</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white"><RegionTicker /></p>
                <p className="text-sm text-gray-500 mt-0.5">Region</p>
              </div>
            </div>
          </div>

          {/* Right — moves slightly slower for depth */}
          <div
            className="relative animate-float-slow"
            style={{ transform: `translateY(${scrollY * -0.04}px)`, willChange: 'transform' }}
          >
            {/* Glow behind card */}
            <div className="absolute -inset-6 bg-gradient-to-r from-indigo-600/20 to-violet-600/20 rounded-3xl blur-2xl" />

            <div className="relative rounded-2xl overflow-hidden border border-white/10 glow-indigo">
              {/* Window chrome */}
              <div className="bg-gray-900/90 backdrop-blur-sm px-4 py-3 flex items-center gap-2 border-b border-white/10">
                <div className="h-3 w-3 rounded-full bg-red-500/70" />
                <div className="h-3 w-3 rounded-full bg-yellow-500/70" />
                <div className="h-3 w-3 rounded-full bg-green-500/70" />
                <span className="ml-3 text-xs text-gray-500 font-mono">example.js</span>
                <div className="ml-auto flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-xs text-emerald-400/70 font-mono">live</span>
                </div>
              </div>

              <pre className="bg-gray-950/80 backdrop-blur-sm px-5 py-5 overflow-x-auto text-xs leading-6 scrollbar-hide">
                <code className="font-mono">
                  {API_CODE.split('\n').map((line, i) => (
                    <div key={i} className="min-h-[1.5rem]">
                      {tokenize(line).map((t, j) => (
                        <span key={j} className={t.cls}>{t.text}</span>
                      ))}
                    </div>
                  ))}
                </code>
              </pre>

              {/* Response badge */}
              <div className="bg-gray-900/90 border-t border-white/10 px-5 py-3 flex items-center justify-between">
                <span className="text-xs text-gray-500 font-mono">response time</span>
                <span className="text-xs font-mono text-emerald-400">83ms ↓</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
